import {
  Component,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
  HostListener,
} from '@angular/core';
import { Subject } from 'rxjs';
import { ChatProvider } from '../shared/services/chat-provider.interface';
import { OriginStatusService } from '../shared/services/origin-status.service';
import {
  SurveyService,
  SurveySubmission,
} from '../shared/services/survey.service';
import {
  SURVEY_OPTIONS,
  AnswerOption,
} from '../shared/constants/survey.constants';
import { Message } from '../shared/models';
import { environment } from '../../environments/environment';
import { AutoResizeDirective } from './auto-resize.directive';
import { finalize, takeUntil } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';
import { InactivityTimerService } from './inactivity-timer.service';

@Component({
  selector: 'app-sparringpartner',
  templateUrl: './sparringpartner.component.html',
  styleUrls: ['./sparringpartner.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class SparringpartnerComponent
  implements OnInit, AfterViewInit, OnDestroy {
  conversationId: string | null = null;
  messages: Message[] = [];
  isThinking: boolean = false;
  inputMessage: string = '';
  showCloseModal: boolean = false;
  showInactivityWarning: boolean = false;
  inactivityCountdown: number = 300;
  conversationTimedOut: boolean = false;
  isEnabled: boolean | null = null;
  private pendingMessage: string | null = null;
  private timerStarted: boolean = false;
  private readonly destroy$ = new Subject<void>();
  readonly isFullSize = window.location.hash.includes('size-full');
  readonly origin = this.getOrigin();
  readonly instance = this.getInstance();

  // Survey state - Map of message index to answer (null if not yet answered)
  surveys = new Map<number, string | null>();

  hasSurveyAfterMessageIndex(index: number): boolean {
    return this.surveys.has(index);
  }

  getSurveyAnswer(index: number): string | null {
    return this.surveys.get(index) ?? null;
  }

  @ViewChild('inputBox') inputBox?: ElementRef<HTMLTextAreaElement>;
  @ViewChild(AutoResizeDirective) autoResizeDirective?: AutoResizeDirective;

  protected readonly environment = environment;
  protected readonly environmentMap = {
    dev: 'Development',
    tst: 'Testing',
    acc: 'Acceptance',
  };
  protected readonly allowedOrigins = [
    'http://localhost:3000',
    'https://wp-acc.uwartsonline.nl',
    'https://sparringpartner-tst.uncinc.nl',
    'https://wp-acc.uwartsonline.nl',
    'https://kadoelerbreek.uwartsonline.nl',
    'https://demo.sparringpartner.digitalevoordeur.amsterdam',
    'https://demo-tst.sparringpartner.digitalevoordeur.amsterdam',
    'https://demo-acc.sparringpartner.digitalevoordeur.amsterdam'
  ];

  constructor(
    private chatProvider: ChatProvider,
    private originStatusService: OriginStatusService,
    private surveyService: SurveyService,
    private inactivityTimerService: InactivityTimerService
  ) { }

  ngOnInit(): void {
    // Start polling origin status and subscribe to changes
    this.originStatusService.startPolling(this.origin);
    this.originStatusService.enabled$.subscribe((enabled) => {
      this.isEnabled = enabled;
    });

    this.chatProvider.receivedMessage().subscribe((message) => {
      this.handleMessage(message);
    });

    this.inactivityTimerService.showWarning$
      .pipe(takeUntil(this.destroy$))
      .subscribe(show => {
        this.showInactivityWarning = show;
        if (show) {
          requestAnimationFrame(() => this.scrollToBottom());
        }
      });

    this.inactivityTimerService.countdown$
      .pipe(takeUntil(this.destroy$))
      .subscribe(seconds => { this.inactivityCountdown = seconds; });

    this.inactivityTimerService.timedOut$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.onInactivityTimeout());

    // If size=full, immediately signal the parent to expand
    if (this.isFullSize) {
      this.allowedOrigins.forEach((origin) => {
        window.parent.postMessage({ type: 'FIRST_MESSAGE_RECEIVED' }, origin);
      });
    }

    // Add a history state to enable back button detection
    window.history.pushState({ modal: false }, '');

    // Listen for back button
    window.addEventListener('popstate', (_event) => {
      if (this.messages.length > 0) {
        // Show modal instead of navigating back
        this.showCloseModal = true;
        // Push state back to prevent actual navigation
        window.history.pushState({ modal: false }, '');
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.inputBox) {
      this.inputBox.nativeElement.focus();
    }
    if (this.autoResizeDirective) {
      this.autoResizeDirective.resetHeight();
    }
  }

  ngOnDestroy(): void {
    this.originStatusService.stopPolling();
    this.inactivityTimerService.stop();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getOrigin(): string {
    let origin = '';
    const params = new URLSearchParams(window.location.search);

    if (params.get('origin') !== null && params.get('origin') !== '') {
      origin = params.get('origin');
    }
    else if (params.get('origin') === null && window.location.href === environment.productionClientUrl) {
      origin = environment.defaultOrigin;
    }
    else {
      origin = environment.defaultOrigin;
      Sentry.captureException('unknown_origin_and_client', {
        extra: {
          href: window.location.href,
          params: params,
        }
      });
    }

    return origin;
  }

  private getInstance(): string {
    let instance = '';
    const params = new URLSearchParams(window.location.search);

    if (params.get('instance') !== null && params.get('instance') !== '') {
      instance = params.get('instance');
    }

    return instance;
  }

  createChat(): void {
    this.chatProvider
      .createChat({
        origin: this.origin,
        instance: this.instance,
        configuration: environment.chatConfiguration,
      })
      .subscribe({
        next: (response) => {
          this.conversationId = response.id;

          // If there's a pending message, send it now
          if (this.pendingMessage) {
            this.inputMessage = this.pendingMessage;
            this.pendingMessage = null;
            this.onSendMessage();
          }
        },
        error: (error) => {
          console.error('Failed to create chat:', error);
        },
      });
  }

  handleMessage(message: Message): void {
    const isFirstMessage = this.messages.length === 0;
    if (message.role === 'assistant') {
      this.isThinking = false;
    }
    this.messages.push(message);

    // Add survey when the API indicates it should be shown
    if (message.role === 'assistant' && message.showSurvey) {
      this.surveys.set(this.messages.length - 1, null);
    }

    if (isFirstMessage && message.role === 'user') {
      const automatedResponse: Message = {
        content:
          'Bedankt voor je bericht. Ik ga je wat vragen stellen en help je verder op weg.\n' +
          '* Ik ben geen vervanging van de huisarts.\n' +
          '* Je kan dit gesprek anoniem houden door niet je naam in te voeren.\n\n' +
          'We zijn de Sparringpartner aan het testen, daarom worden de antwoorden gecontroleerd door een onafhankelijke zorgprofessional. ' +
          'Voor meer informatie:\n[Lees onze privacyverklaring](https://www.digitalevoordeur.amsterdam/privacyverklaring-sparringpartner).',
        role: 'assistant',
        createdAt: new Date().toISOString(),
      };
      this.messages.push(automatedResponse);
    }

    if (isFirstMessage) {
      this.allowedOrigins.forEach((origin) => {
        window.parent.postMessage(
          {
            type: 'FIRST_MESSAGE_RECEIVED',
          },
          origin
        );
      });
    }

    requestAnimationFrame(() => {
      if (message.role === 'assistant') {
        this.scrollToMessage(this.messages.length - 1);
      } else {
        this.scrollToBottom();
      }
    });
  }

  onSendMessage(): void {
    if (this.inputMessage?.trim().length) {
      if (!this.conversationId) {
        this.pendingMessage = this.inputMessage;
        this.isThinking = true;
        this.createChat();
        return;
      }
      if (this.isThinking && this.pendingMessage) {
        this.inputMessage = this.pendingMessage;
      }
      const message = this.inputMessage;
      this.inputMessage = '';
      this.isThinking = true;

      if (!this.timerStarted) {
        this.inactivityTimerService.start();
        this.timerStarted = true;
      } else {
        this.inactivityTimerService.reset();
      }

      this.chatProvider.sendMessage(this.conversationId, message).subscribe({
        error: (error) => {
          console.error('Failed to send message:', error);
          this.isThinking = false;
        },
      });

      if (this.autoResizeDirective) {
        this.autoResizeDirective.resetHeight();
      }

      requestAnimationFrame(() => {
        this.scrollToBottom();
      });
    }
  }

  onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.onSendMessage();
    }
  }

  scrollToBottom(): void {
    const messagesEl = document.getElementById('messages');
    if (messagesEl) {
      messagesEl.scrollTop = messagesEl.scrollHeight;
    }
  }

  scrollToMessage(index: number): void {
    const messageEl = document.getElementById(`message-${index}`);
    if (messageEl) {
      messageEl.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }

  onCloseButtonClick(): void {
    if (this.messages.length > 0) {
      this.showCloseModal = true;

      return;
    }

    this.closeChat();
  }

  closeChat(): void {
    this.allowedOrigins.forEach((origin) => {
      window.parent.postMessage(
        {
          type: 'CHAT_CLOSED',
        },
        origin
      );
    });
  }

  onConfirmClose(): void {
    this.showCloseModal = false;
    this.inactivityTimerService.stop();

    // TODO: sendConclusionMessage disabled for now
    // if (this.conversationId && !this.conversationTimedOut) {
    //   this.chatProvider
    //     .sendConclusionMessage(this.conversationId)
    //     .pipe(finalize(() => this.closeChat()))
    //     .subscribe({
    //       error: (error) => {
    //         Sentry.captureException(error, {
    //           extra: { conversationId: this.conversationId },
    //         });
    //       },
    //     });
    //   return;
    // }

    this.closeChat();
  }

  onInactivityTimeout(): void {
    this.inactivityTimerService.stop();
    this.conversationTimedOut = true;

    if (this.conversationId) {
      this.chatProvider
        .sendTimeoutMessage(this.conversationId)
        .subscribe({
          error: (error) => {
            Sentry.captureException(error, {
              extra: { conversationId: this.conversationId },
            });
          },
        });
    }
  }

  onStartNewConversation(): void {
    this.conversationId = null;
    this.messages = [];
    this.isThinking = false;
    this.inputMessage = '';
    this.showCloseModal = false;
    this.showInactivityWarning = false;
    this.inactivityCountdown = 300;
    this.conversationTimedOut = false;
    this.timerStarted = false;
    this.pendingMessage = null;
    this.surveys.clear();

    requestAnimationFrame(() => {
      if (this.inputBox) {
        this.inputBox.nativeElement.focus();
      }
    });
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: Event): void {
    if (this.showCloseModal) {
      this.showCloseModal = false;
      event.preventDefault();
    }
  }

  private readonly surveyQuestion =
    'In hoeverre ben je op dit moment geholpen?';

  onSurveyAnswered(messageIndex: number, answer: string): void {
    if (this.surveys.has(messageIndex) && this.conversationId) {
      this.surveys.set(messageIndex, answer);
      const option = SURVEY_OPTIONS[answer] as AnswerOption;
      const survey: SurveySubmission = {
        question: this.surveyQuestion,
        skipped: false,
        score: option.score,
        description: option.label,
      };
      this.surveyService.submitSurvey(this.conversationId, survey).subscribe({
        error: (error) => {
          Sentry.captureException(error, {
            extra: {
              conversationId: this.conversationId,
              messageIndex,
              answer,
            },
          });
        },
      });
    }
  }

  onSurveySkipped(messageIndex: number): void {
    if (this.conversationId) {
      const survey: SurveySubmission = {
        question: this.surveyQuestion,
        skipped: true,
      };
      this.surveyService.submitSurvey(this.conversationId, survey).subscribe({
        error: (error) => {
          Sentry.captureException(error, {
            extra: {
              conversationId: this.conversationId,
              messageIndex,
            },
          });
        },
      });
    }
    this.surveys.set(messageIndex, 'skipped');
  }
}
